import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MdLocationOn, MdCalendarToday, MdChecklist, MdCheckCircle, MdCameraAlt, MdClose, MdAttachFile } from 'react-icons/md';
import { compressImage } from '../utils/imageUtils';

const LocationIcon  = MdLocationOn as any;
const CalendarIcon  = MdCalendarToday as any;
const ChecklistIcon = MdChecklist as any;
const CheckCircleIcon = MdCheckCircle as any;
const CameraIcon    = MdCameraAlt as any;
const CloseIcon     = MdClose as any;
const AttachIcon    = MdAttachFile as any;

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE = process.env.REACT_APP_IMAGE_BASE_URL || '';

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #E5E7EB',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  color: '#374151',
  fontFamily: 'inherit',
  backgroundColor: '#fff',
};

// ── Checklist value input (same types as DetailSurveyActivity) ─────────────────

interface ChecklistItem {
  id: number;
  group_name: string | null;
  description: string | null;
  input_type: string;
  options: string[] | null;
  value: string | null;
}

interface ChecklistInputProps {
  item: ChecklistItem;
  onChange: (value: string) => void;
}

const ChecklistInput: React.FC<ChecklistInputProps> = ({ item, onChange }) => {
  const options = Array.isArray(item.options) ? item.options : [];
  const inputType = item.input_type || 'text';

  const selInitOther = inputType === 'select' && item.value !== '' && item.value !== null && !options.includes(item.value || '');
  const [selVal, setSelVal] = useState(selInitOther ? '__other__' : item.value || '');
  const [selOther, setSelOther] = useState(selInitOther ? (item.value || '') : '');

  const radioInitOther = inputType === 'radio' && item.value !== '' && item.value !== null && !options.includes(item.value || '');
  const [radioSel, setRadioSel] = useState(radioInitOther ? '__other__' : item.value || '');
  const [radioOther, setRadioOther] = useState(radioInitOther ? (item.value || '') : '');

  const parsedCb: string[] = (() => {
    if (inputType !== 'checkbox') return [];
    try { const a = JSON.parse(item.value || ''); return Array.isArray(a) ? a : []; }
    catch { return item.value ? [item.value] : []; }
  })();
  const [cbChecked, setCbChecked] = useState<Set<string>>(new Set(parsedCb.filter(v => options.includes(v))));
  const [cbOtherOn, setCbOtherOn] = useState(parsedCb.some(v => !options.includes(v)));
  const [cbOther, setCbOther] = useState(parsedCb.find(v => !options.includes(v)) || '');

  const buildCb = (ch: Set<string>, isOther: boolean, oText: string) => {
    const arr = [...options.filter(o => ch.has(o))];
    if (isOther && oText.trim()) arr.push(oText.trim());
    return arr.length ? JSON.stringify(arr) : '';
  };

  if (inputType === 'select') {
    return (
      <div>
        <select value={selVal} onChange={e => { const v = e.target.value; setSelVal(v); onChange(v === '__other__' ? selOther : v); }} style={{ ...inputBaseStyle, cursor: 'pointer' }}>
          <option value="">— Pilih —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
          <option value="__other__">Lainnya</option>
        </select>
        {selVal === '__other__' && (
          <input type="text" value={selOther} onChange={e => { setSelOther(e.target.value); onChange(e.target.value); }} placeholder="Masukkan nilai..." style={{ ...inputBaseStyle, marginTop: 8 }} />
        )}
      </div>
    );
  }

  if (inputType === 'radio') {
    return (
      <div>
        {[...options, '__other__'].map(opt => {
          const isOther = opt === '__other__';
          const isSelected = radioSel === opt;
          return (
            <div key={opt}>
              <div onClick={() => { setRadioSel(opt); onChange(isOther ? radioOther : opt); }} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer', userSelect: 'none' }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${isSelected ? '#3B82F6' : '#D1D5DB'}`, backgroundColor: isSelected ? '#3B82F6' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {isSelected && <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#fff' }} />}
                </div>
                <span style={{ fontSize: 14, color: isSelected ? '#111827' : '#374151', fontWeight: isSelected ? 600 : 400 }}>{isOther ? 'Lainnya' : opt}</span>
              </div>
              {isOther && isSelected && (
                <input type="text" value={radioOther} onChange={e => { setRadioOther(e.target.value); onChange(e.target.value); }} placeholder="Masukkan nilai..." style={{ ...inputBaseStyle, marginBottom: 8, marginLeft: 28, width: 'calc(100% - 28px)' }} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (inputType === 'checkbox') {
    return (
      <div>
        {[...options, '__other__'].map(opt => {
          const isOther = opt === '__other__';
          const isChecked = isOther ? cbOtherOn : cbChecked.has(opt);
          return (
            <div key={opt}>
              <div onClick={() => {
                if (isOther) { const n = !cbOtherOn; setCbOtherOn(n); onChange(buildCb(cbChecked, n, cbOther)); }
                else { const n = new Set(cbChecked); if (n.has(opt)) n.delete(opt); else n.add(opt); setCbChecked(n); onChange(buildCb(n, cbOtherOn, cbOther)); }
              }} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer', userSelect: 'none' }}>
                <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${isChecked ? '#3B82F6' : '#D1D5DB'}`, backgroundColor: isChecked ? '#3B82F6' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {isChecked && <svg width="11" height="8" viewBox="0 0 11 8" fill="none"><path d="M1 3.5L4 6.5L10 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <span style={{ fontSize: 14, color: isChecked ? '#111827' : '#374151', fontWeight: isChecked ? 600 : 400 }}>{isOther ? 'Lainnya' : opt}</span>
              </div>
              {isOther && isChecked && (
                <input type="text" value={cbOther} onChange={e => { setCbOther(e.target.value); onChange(buildCb(cbChecked, cbOtherOn, e.target.value)); }} placeholder="Masukkan nilai..." style={{ ...inputBaseStyle, marginBottom: 8, marginLeft: 28, width: 'calc(100% - 28px)' }} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // text (default)
  return (
    <textarea
      value={item.value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder="Nilai / Keterangan..."
      style={{ width: '100%', minHeight: 80, padding: '8px 12px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 14, resize: 'none', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5, color: '#374151', fontFamily: 'inherit' }}
    />
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

type PageState = 'loading' | 'error' | 'form' | 'success';

export const PublicSurvey: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [survey, setSurvey] = useState<any>(null);
  const [checklists, setChecklists] = useState<ChecklistItem[]>([]);
  const [values, setValues] = useState<Record<number, string>>({});
  const [photoFiles, setPhotoFiles] = useState<Record<number, File[]>>({});
  const [photoPreviews, setPhotoPreviews] = useState<Record<number, string[]>>({});
  const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);
  const [additionalPreviews, setAdditionalPreviews] = useState<string[]>([]);
  const [respondentName, setRespondentName] = useState('');
  const [respondentContact, setRespondentContact] = useState('');
  const [submittedLocation, setSubmittedLocation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!token) { setErrorMsg('Invalid link.'); setPageState('error'); return; }
    fetch(`${API_BASE}/survey-activities/public/${token}`)
      .then(async r => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          if (r.status === 410) setErrorMsg(data.message === 'Survey already submitted' ? 'Survey ini sudah disubmit.' : 'Link survey ini sudah kedaluwarsa.');
          else if (r.status === 403) setErrorMsg('Link survey ini tidak aktif.');
          else setErrorMsg('Survey tidak ditemukan.');
          setPageState('error');
          return;
        }
        const data = await r.json();
        setSurvey(data);
        setChecklists(data.checklists || []);
        const init: Record<number, string> = {};
        (data.checklists || []).forEach((c: ChecklistItem) => { init[c.id] = c.value || ''; });
        setValues(init);
        setPageState('form');

        // Capture location in background
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(pos => {
            setSubmittedLocation(`${pos.coords.latitude},${pos.coords.longitude}`);
          }, () => {});
        }
      })
      .catch(() => { setErrorMsg('Gagal memuat survey. Periksa koneksi Anda.'); setPageState('error'); });
  }, [token]);

  const handlePhotoSelect = async (cid: number, files: FileList) => {
    const compressed = await Promise.all(Array.from(files).map(f => compressImage(f).catch(() => f)));
    const newPreviews = compressed.map(f => URL.createObjectURL(f));
    setPhotoFiles(prev => ({ ...prev, [cid]: [...(prev[cid] || []), ...compressed] }));
    setPhotoPreviews(prev => ({ ...prev, [cid]: [...(prev[cid] || []), ...newPreviews] }));
  };

  const handlePhotoRemove = (cid: number, index: number) => {
    setPhotoFiles(prev => ({ ...prev, [cid]: (prev[cid] || []).filter((_, i) => i !== index) }));
    setPhotoPreviews(prev => {
      const current = prev[cid] || [];
      if (current[index]) URL.revokeObjectURL(current[index]);
      return { ...prev, [cid]: current.filter((_, i) => i !== index) };
    });
  };

  const handleAdditionalFilesSelect = async (files: FileList) => {
    const compressed = await Promise.all(Array.from(files).map(f => compressImage(f).catch(() => f)));
    const newPreviews = compressed.map(f => URL.createObjectURL(f));
    setAdditionalFiles(prev => [...prev, ...compressed]);
    setAdditionalPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleAdditionalFileRemove = (index: number) => {
    setAdditionalFiles(prev => prev.filter((_, i) => i !== index));
    setAdditionalPreviews(prev => {
      if (prev[index]) URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async () => {
    const errs: Record<string, string> = {};
    if (!respondentName.trim()) errs.name = 'Mohon masukkan nama Anda.';
    if (!respondentContact.trim()) errs.contact = 'Mohon masukkan nomor HP atau email Anda.';
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
    setFieldErrors({});

    const checklistValues = checklists.map(c => ({ id: c.id, value: values[c.id] || '' }));

    const fd = new FormData();
    fd.append('respondent_name', respondentName.trim());
    fd.append('respondent_contact', respondentContact.trim());
    if (submittedLocation) fd.append('submitted_location', submittedLocation);
    fd.append('checklist_values', JSON.stringify(checklistValues));
    for (const [cid, files] of Object.entries(photoFiles)) {
      for (const file of files) fd.append(`image_${cid}`, file);
    }
    for (const file of additionalFiles) {
      fd.append('additional_attachment', file);
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/survey-activities/public/${token}/submit`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.message || 'Gagal mengirim survey.');
        setPageState('error');
        return;
      }
      setPageState('success');
    } catch {
      setErrorMsg('Gagal mengirim. Periksa koneksi Anda.');
      setPageState('error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d: string) =>
    new Date(d.slice(0, 10) + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // ── Loading ──
  if (pageState === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FAFB' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #E5E7EB', borderTopColor: '#3B82F6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#9CA3AF', fontSize: 14 }}>Memuat survey...</p>
        </div>
      </div>
    );
  }

  // ── Error / Expired ──
  if (pageState === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FAFB', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🔗</div>
          <h2 style={{ color: '#111827', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Link Tidak Tersedia</h2>
          <p style={{ color: '#6B7280', fontSize: 14, lineHeight: 1.6 }}>{errorMsg}</p>
        </div>
      </div>
    );
  }

  // ── Success ──
  if (pageState === 'success') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FAFB', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <CheckCircleIcon size={72} color="#10B981" style={{ marginBottom: 16 }} />
          <h2 style={{ color: '#111827', fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Terima Kasih!</h2>
          <p style={{ color: '#6B7280', fontSize: 14, lineHeight: 1.6 }}>Jawaban survey Anda telah berhasil dikirim.</p>
        </div>
      </div>
    );
  }

  // ── Form ──
  const grouped = checklists.reduce<Record<string, ChecklistItem[]>>((acc, c) => {
    const g = c.group_name || '';
    if (!acc[g]) acc[g] = [];
    acc[g].push(c);
    return acc;
  }, {});

  return (
    <div style={{ backgroundColor: '#F9FAFB', minHeight: '100vh', paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ backgroundColor: '#3B82F6', padding: '20px 16px 24px' }}>
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
          Formulir Survey
        </p>
        <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0, textTransform: 'capitalize' }}>
          {survey.location_name || 'Survey'}
        </h1>
        {survey.survey_date && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
            <CalendarIcon size={14} color="rgba(255,255,255,0.8)" />
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>{formatDate(survey.survey_date)}</span>
          </div>
        )}
        {survey.address && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 4 }}>
            <LocationIcon size={14} color="rgba(255,255,255,0.8)" style={{ marginTop: 2, flexShrink: 0 }} />
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, lineHeight: 1.5 }}>{survey.address}</span>
          </div>
        )}
      </div>

      <div style={{ padding: '20px 16px' }}>
        {/* Respondent info */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, border: '1px solid #E5E7EB' }}>
          <h2 style={{ color: '#111827', fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Informasi Anda</h2>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Nama Lengkap <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              type="text"
              value={respondentName}
              onChange={e => { setRespondentName(e.target.value); setFieldErrors(prev => ({ ...prev, name: '' })); }}
              placeholder="Masukkan nama lengkap Anda"
              style={{ ...inputBaseStyle, border: fieldErrors.name ? '1px solid #EF4444' : '1px solid #E5E7EB' }}
            />
            {fieldErrors.name && <p style={{ color: '#EF4444', fontSize: 12, marginTop: 4 }}>{fieldErrors.name}</p>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Telepon / Email <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              type="text"
              value={respondentContact}
              onChange={e => { setRespondentContact(e.target.value); setFieldErrors(prev => ({ ...prev, contact: '' })); }}
              placeholder="Masukkan nomor HP atau email"
              style={{ ...inputBaseStyle, border: fieldErrors.contact ? '1px solid #EF4444' : '1px solid #E5E7EB' }}
            />
            {fieldErrors.contact && <p style={{ color: '#EF4444', fontSize: 12, marginTop: 4 }}>{fieldErrors.contact}</p>}
          </div>

          {submittedLocation && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '8px 10px', backgroundColor: '#F0FDF4', borderRadius: 8 }}>
              <LocationIcon size={14} color="#10B981" />
              <span style={{ fontSize: 12, color: '#065F46' }}>Lokasi berhasil ditangkap</span>
            </div>
          )}
        </div>

        {/* Checklist sections */}
        {checklists.length > 0 && (
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 20, border: '1px solid #E5E7EB' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <ChecklistIcon size={18} color="#3B82F6" />
              <h2 style={{ color: '#111827', fontSize: 15, fontWeight: 700, margin: 0 }}>Survey Checklist</h2>
            </div>

            {Object.entries(grouped).map(([groupName, items]) => (
              <div key={groupName} style={{ marginBottom: 20 }}>
                {groupName && (
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#3B82F6', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #EFF6FF' }}>
                    {groupName}
                  </div>
                )}
                {items.map(item => (
                  <div key={item.id} style={{ marginBottom: 16 }}>
                    {item.description && (
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                        {item.description}
                      </label>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {/* Photo upload (above input) — bisa lebih dari 1 foto */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {(photoPreviews[item.id] || []).map((preview, idx) => (
                          <div key={idx} style={{ position: 'relative', width: 60, height: 60 }}>
                            <img
                              src={preview}
                              alt=""
                              style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 10, display: 'block' }}
                            />
                            <div
                              onClick={() => handlePhotoRemove(item.id, idx)}
                              style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', backgroundColor: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                              <CloseIcon size={11} color="#fff" />
                            </div>
                          </div>
                        ))}
                        <label style={{ width: 60, height: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, borderRadius: 10, border: '1.5px dashed #D1D5DB', cursor: 'pointer', backgroundColor: '#F9FAFB' }}>
                          <CameraIcon size={18} color="#9CA3AF" />
                          <span style={{ fontSize: 9, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.1 }}>Foto</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: 'none' }}
                            onChange={e => {
                              if (e.target.files && e.target.files.length > 0) handlePhotoSelect(item.id, e.target.files);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>

                      {/* Input (below photos) */}
                      <ChecklistInput
                        item={{ ...item, value: values[item.id] ?? (item.value || '') }}
                        onChange={val => setValues(prev => ({ ...prev, [item.id]: val }))}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Additional attachment — lampiran umum, tidak terikat ke checklist item manapun */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 20, border: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <AttachIcon size={18} color="#3B82F6" />
            <h2 style={{ color: '#111827', fontSize: 15, fontWeight: 700, margin: 0 }}>Lampiran Tambahan</h2>
          </div>
          <p style={{ color: '#9CA3AF', fontSize: 12, marginBottom: 14, marginTop: 0 }}>
            Opsional — unggah foto atau dokumen pendukung lainnya.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {additionalPreviews.map((preview, idx) => (
              <div key={idx} style={{ position: 'relative', width: 72, height: 72 }}>
                <img
                  src={preview}
                  alt=""
                  style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 10, display: 'block' }}
                />
                <div
                  onClick={() => handleAdditionalFileRemove(idx)}
                  style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', backgroundColor: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <CloseIcon size={12} color="#fff" />
                </div>
              </div>
            ))}
            <label style={{ width: 72, height: 72, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, borderRadius: 10, border: '1.5px dashed #D1D5DB', cursor: 'pointer', backgroundColor: '#F9FAFB' }}>
              <CameraIcon size={20} color="#9CA3AF" />
              <span style={{ fontSize: 10, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.2 }}>Tambah</span>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => {
                  if (e.target.files && e.target.files.length > 0) handleAdditionalFilesSelect(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%', padding: '15px 0', borderRadius: 14, border: 'none',
            backgroundColor: submitting ? '#93C5FD' : '#3B82F6', color: '#fff',
            fontSize: 16, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
          }}
        >
          {submitting ? 'Mengirim...' : 'Submit Survey'}
        </button>
      </div>
    </div>
  );
};
